import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { DEFAULT_THEME, applyTheme, directionFor, resolveMode } from "./tokens";
import type { Locale, ResolvedMode, TenantTheme, ThemeMode } from "./tokens";

interface ThemeContextValue {
  theme: TenantTheme;
  resolvedMode: ResolvedMode;
  /** Merge a partial patch into the active theme (derives `dir` from `locale`). */
  setTheme: (patch: Partial<TenantTheme>) => void;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
  setLocale: (locale: Locale) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({
  initial,
  onChange,
  children,
}: {
  initial?: Partial<TenantTheme>;
  onChange?: (theme: TenantTheme) => void;
  children: ReactNode;
}) {
  const [theme, setThemeState] = useState<TenantTheme>(() => ({ ...DEFAULT_THEME, ...initial }));
  const [resolvedMode, setResolvedMode] = useState<ResolvedMode>(() => resolveMode(theme.mode));
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const setTheme = useCallback((patch: Partial<TenantTheme>) => {
    setThemeState((prev) => {
      const next: TenantTheme = { ...prev, ...patch };
      if (patch.locale !== undefined && patch.dir === undefined) {
        next.dir = directionFor(patch.locale);
      }
      return next;
    });
  }, []);

  const setMode = useCallback((mode: ThemeMode) => setTheme({ mode }), [setTheme]);
  const setLocale = useCallback((locale: Locale) => setTheme({ locale }), [setTheme]);
  const toggleMode = useCallback(() => {
    setThemeState((prev) => ({ ...prev, mode: prev.mode === "dark" ? "light" : "dark" }));
  }, []);

  // Apply theme to the document root whenever it changes (live, no refresh).
  useEffect(() => {
    const resolved = applyTheme(theme);
    setResolvedMode(resolved);
    onChangeRef.current?.(theme);
  }, [theme]);

  const ctx = useMemo<ThemeContextValue>(
    () => ({ theme, resolvedMode, setTheme, setMode, toggleMode, setLocale }),
    [theme, resolvedMode, setTheme, setMode, toggleMode, setLocale],
  );

  return <ThemeContext.Provider value={ctx}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within <ThemeProvider>");
  return ctx;
}
